import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { ArrowLeftIcon, BookmarkIcon, Share2Icon } from 'lucide-react';
import { Logo } from './Logo';
import { InviteWall } from './InviteWall';
export type ArticleContent = {
  slug: string;
  category: string;
  title: string;
  dek: string;
  byline: string;
  date: string;
  readTime: string;
  image: string;
  alt: string;
  caption: string;
  body: string[];
};
type ArticleViewProps = {
  article: ArticleContent;
  siblings: ArticleContent[];
  basePath: string;
  backLabel: string;
  backHref: string;
  moreHeading: string;
  freeParagraphs?: number;
  wallAtPx?: number;
};
export function ArticleView({
  article,
  siblings,
  basePath,
  backLabel,
  backHref,
  moreHeading,
  freeParagraphs = 2,
  wallAtPx = 420
}: ArticleViewProps) {
  const [walledRaw, setWalled] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const walled = walledRaw && !dismissed;
  useEffect(() => {
    window.scrollTo(0, 0);
    setWalled(false);
    setDismissed(false);
    document.title = `${article.title} — Superluxury`;
  }, [article]);
  useEffect(() => {
    if (walledRaw) return;
    const onScroll = () => {
      if (window.scrollY > wallAtPx) setWalled(true);
    };
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', onScroll);
  }, [walledRaw, wallAtPx, article]);

  // Hold the page in place once the wall is up
  useEffect(() => {
    if (!walled) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [walled]);
  const free = article.body.slice(0, freeParagraphs);
  const reserved = article.body.slice(freeParagraphs);
  const more = siblings.filter(s => s.slug !== article.slug).slice(0, 3);
  return <>
      <header className="border-b border-line bg-paper" data-id="element-95" data-mp="23o9hcj063wwb">
        <div className="mx-auto grid h-16 max-w-[1320px] grid-cols-[1fr_auto_1fr] items-center px-6 md:h-20 md:px-10" data-id="element-96" data-mp="23nrbqvrm86bq">
          <Link to={backHref} className="inline-flex items-center gap-2 text-[14px] font-light text-ink/70 transition-colors duration-150 hover:text-ink" data-id="element-97" data-mp="28pgoqgyeeu2g">
            <ArrowLeftIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-98" data-mp="2hn957ofuw7rf" />
            <span className="hidden sm:inline" data-id="element-99" data-mp="1kzcp0kobzegq">{backLabel}</span>
          </Link>
          <Link to="/" aria-label="Superluxury — home" data-id="element-100" data-mp="1pxxo2grujs3l">
            <Logo className="text-[16px] md:text-[22px]" data-id="element-101" data-mp="1tyhmros2j3ru" />
          </Link>
          <div className="flex justify-end" data-id="element-102" data-mp="1twd769kt4gbq">
            <Link to="/" className="text-[14px] font-medium" data-id="element-103" data-mp="1yppwyn182t62">
              Log in
            </Link>
          </div>
        </div>
        <nav aria-label={`${backLabel} sections`} className="hidden border-t border-line md:block" data-id="element-104" data-mp="126if455kecjd">
          <ul className="mx-auto flex max-w-[1320px] justify-center gap-8 px-10 py-3 text-[13px] font-light text-ink/70" data-id="element-105" data-mp="167c657eb07gh">
            {siblings.map(s => <li key={s.slug} data-id="element-106" data-mp="tprmx3fi5y97">
                <Link to={`${basePath}/${s.slug}`} className={`whitespace-nowrap transition-colors duration-150 hover:text-ink ${s.slug === article.slug ? 'font-medium text-ink' : ''}`} data-id="element-107" data-mp="trkt6hm7eggo">
                  {s.category}
                </Link>
              </li>)}
          </ul>
        </nav>
      </header>

      <main className="bg-paper pb-24" data-id="element-108" data-mp="az9uz12hv6ow">
        <article data-id="element-109" data-mp="ay8n15grz0be">
          <header className="mx-auto max-w-[720px] px-6 pt-12 md:pt-16" data-id="element-110" data-mp="axp2dgzqpxto">
            <p className="text-[14px] font-medium" data-id="element-111" data-mp="618dulomphg2">{article.category}</p>
            <h1 className="mt-4 font-editorial text-[38px] leading-[42px] tracking-[-0.015em] md:text-[54px] md:leading-[58px]" data-id="element-112" data-mp="1zwmaam72m58">
              {article.title}
            </h1>
            <p className="mt-5 text-[19px] font-light leading-[29px] text-ink/75 md:text-[21px] md:leading-[31px]" data-id="element-113" data-mp="3o6lfkcu5ayv5">{article.dek}</p>
            <div className="mt-8 flex items-center justify-between gap-4 border-y border-line py-4" data-id="element-114" data-mp="1s48ariinn5z9">
              <div className="text-[14px]" data-id="element-115" data-mp="1x18idxv5kgsh">
                <p className="font-medium" data-id="element-116" data-mp="1x42y5c381dfk">{article.byline}</p>
                <p className="mt-0.5 font-light text-ink/60" data-id="element-117" data-mp="1ickkhkxu3uzd">
                  {article.date} · {article.readTime}
                </p>
              </div>
              <div className="flex gap-2" data-id="element-118" data-mp="19cxj2z53up6b">
                <button type="button" aria-label="Share" className="flex h-9 w-9 items-center justify-center border border-line transition-colors duration-150 hover:border-ink" data-id="element-119" data-mp="19bw5amwpfokw">
                  <Share2Icon className="h-4 w-4" strokeWidth={1.5} data-id="element-120" data-mp="zfazhzcv29g0" />
                </button>
                <button type="button" aria-label="Save" className="flex h-9 w-9 items-center justify-center border border-line transition-colors duration-150 hover:border-ink" data-id="element-121" data-mp="14ct3rcfxo0ho">
                  <BookmarkIcon className="h-4 w-4" strokeWidth={1.5} data-id="element-122" data-mp="2yrdk5cx819v9" />
                </button>
              </div>
            </div>
          </header>

          <figure className="mx-auto mt-10 max-w-[1080px] md:px-10" data-id="element-123" data-mp="n4s3lee3rnja">
            <div className="aspect-[16/9] overflow-hidden bg-mist" data-id="element-124" data-mp="i5ysttq7aamt">
              <img src={article.image} alt={article.alt} className="h-full w-full object-cover" data-id="element-125" data-mp="wu3cazx2uq6n" />
            </div>
            <figcaption className="mx-auto mt-3 max-w-[720px] px-6 text-[13px] font-light leading-[20px] text-ink/60 md:px-0" data-id="element-126" data-mp="s2tom4v39wxe">
              {article.caption}
            </figcaption>
          </figure>

          <div className="relative mx-auto mt-12 max-w-[720px] px-6" data-id="element-127" data-mp="10w6ppu3b44uk">
            <div className="space-y-6 font-editorial text-[20px] leading-[33px] text-ink/90" data-id="element-128" data-mp="10y1ejjhp8h3x">
              {free.map(p => <p key={p} data-id="element-129" data-mp="1atbb3rpbvwab">{p}</p>)}
              {/* Reserved for members — shown only as a faded suggestion of the text */}
              <div aria-hidden="true" className="select-none space-y-6 blur-[3px]" data-id="element-130" data-mp="3hkklyljk05ok">
                {reserved.map(p => <p key={p} data-id="element-131" data-mp="3cnkfwampf2xg">{p}</p>)}
              </div>
            </div>
            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-3/5 bg-gradient-to-t from-paper via-paper/90 to-transparent" aria-hidden="true" data-id="element-132" data-mp="661zc3yt5568" />
          </div>
        </article>

        <section aria-labelledby="more-heading" className="mx-auto mt-16 max-w-[1080px] px-6 md:px-10" data-id="element-133" data-mp="f6qgh7jhzlnc">
          <h2 id="more-heading" className="border-t border-ink pt-4 text-[15px] font-medium" data-id="element-134" data-mp="k1nqohsik11t">
            {moreHeading}
          </h2>
          <ul className="mt-6 grid grid-cols-1 gap-8 md:grid-cols-3" data-id="element-135" data-mp="t3wvqs50j27t">
            {more.map(s => <li key={s.slug} data-id="element-136" data-mp="xun5ehkh1r8b">
                <Link to={`${basePath}/${s.slug}`} className="group block" data-id="element-137" data-mp="xvordh30zy4t">
                  <div className="aspect-[16/10] overflow-hidden bg-mist" data-id="element-138" data-mp="12t6p0bbleqxi">
                    <img src={s.image} alt="" className="h-full w-full object-cover transition-transform duration-[1200ms] ease-luxe group-hover:scale-[1.04]" data-id="element-139" data-mp="17tkw9r5ddwuh" />
                  </div>
                  <p className="mt-4 text-[13px] font-light text-ink/60" data-id="element-140" data-mp="2xaghzptlains">{s.category}</p>
                  <h3 className="mt-1 font-editorial text-[22px] leading-[27px] transition-opacity duration-300 group-hover:opacity-70" data-id="element-141" data-mp="2x83smrli560p">
                    {s.title}
                  </h3>
                </Link>
              </li>)}
          </ul>
        </section>
      </main>

      <AnimatePresence data-id="element-142" data-mp="2ifacr52binvi">{walled && <InviteWall onClose={() => setDismissed(true)} data-id="element-143" data-mp="3g0ohlxy2yqz8" />}</AnimatePresence>
    </>;
}